import { DB } from "@p0security/frontend-core/react";
import { FrontendInstallContext } from "@p0security/integrations-installer";
import {
  anyIsInstalled,
  installedItems,
} from "@p0security/shared/install/installed";
import { ConfigOf } from "@p0security/shared/install/types";
import { integrationLabel } from "@p0security/shared/integrations/registry";
import { isPermissionType } from "@p0security/shared/types/permission";
import { Flex } from "@p0security/ui";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { sortBy } from "lodash";

import { AllIntegrationLogos } from "../IntegrationLogo.tsx";
import { resourceIntegrations } from "../constants.tsx";
import { ExtendedIntegrationData } from "../types.ts";

export const numberOfResourceInstalled = (
  extendedData: ExtendedIntegrationData[]
) =>
  extendedData.filter((d) =>
    Object.values(d).some(
      (v) => typeof v === "object" && "installed" in v && v?.installed > 0
    )
  ).length;

// Re-exported from `@p0security/integrations-installer` so existing
// `frontend` call sites keep working unchanged.
export { integrationOptionProvider } from "@p0security/integrations-installer";

/** An option provider that lists components from another installed integration.
 *
 * Makes a single read from Firestore when options are listed.
 */
export const otherInstalledIntegration =
  (args: { integration: string; component: string }) =>
  async ({ tenantId }: FrontendInstallContext<any>) => {
    const aws = await getDoc(
      doc(DB, `o/${tenantId}/integrations/${args.integration}`)
    );
    const data = aws.data();
    if (!data) return [];
    return sortBy(
      Object.entries(
        installedItems(args.component, data as ConfigOf<Record<string, any>>)
      ).map(([id, item]) => ({
        id,
        label: item?.label ? `${item.label} (${id})` : id,
      })),
      "label"
    );
  };

/** An option provider that lists installed integrations */
export const installedIntegrations = async ({
  featureFlags,
  tenantId,
}: FrontendInstallContext<any>) => {
  const integrations = await getDocs(
    collection(DB, `o/${tenantId}/integrations`)
  );
  const mapped = Object.fromEntries(
    integrations.docs.map((d) => [d.id, d.data()])
  );
  const allResources = resourceIntegrations(featureFlags);
  const selectable = allResources
    .filter((r) => mapped[r.key] && anyIsInstalled(mapped[r.key]))
    .map((r) => ({
      id: r.key,
      label: integrationLabel(r.key),
    }));
  return sortBy(selectable, "label").map((r) => ({
    ...r,
    label: isPermissionType(r.id) && (
      <Flex gap={8} align="center">
        {AllIntegrationLogos[r.id]}
        {r.label}
      </Flex>
    ),
  }));
};
